import React, { useEffect, useState, useContext } from 'react';
import { RouteComponentProps, useLocation, useHistory } from 'react-router';
import { toast } from 'react-toastify';
import CopyToClipboard from 'react-copy-to-clipboard';
import {
  CircularProgress,
  Typography,
  Button,
  Grid,
  Tooltip,
} from '@material-ui/core';
import queryString from 'query-string';
import { instance } from '../../services/api';
import { getISODateTimeString } from '../../utils/dateUtils';
import { GlobalsContext } from '../../store/Globals';
import { FieldView } from '../../types/FieldView';
import SingleField from './SingleField';
import QrCodeDialog from './QrCodeDialog';

import {
  Paper,
  Container,
  Title,
  LoadingContainer,
  FieldContainer,
  Icon,
} from './styles';

const ItemScreen: React.FC<RouteComponentProps<{ key: string }>> = ({
  match,
}) => {
  const globals = useContext(GlobalsContext);
  const { params } = match;
  const location = useLocation();
  const history = useHistory();

  const [title, setTitle] = useState<string>('');
  const [fieldState, setFieldState] = useState<FieldView[]>([]);
  const [loading, setLoading] = useState(true);
  const [isPrivate, setIsPrivate] = useState(false);
  const [openQRCode, setOpenQRCode] = useState(false);
  const [copied, setCopied] = useState(false);
  const [collection, setCollection] = useState<string>('');

  useEffect(() => {
    const objectToSchema = (obj: object, tab: number): FieldView[] => {
      if (!obj) return [];
      let fields: FieldView[] = [];

      const objItens = Object.entries(obj);
      objItens.forEach((item: any) => {
        if (typeof item[1] === 'object') {
          fields = fields.concat(objectToSchema(item[1], tab + 1));
        } else {
          fields.push({
            label: item[0],
            value:
              item.length > 1
                ? typeof item[1] === 'string' || typeof item[1] === 'number'
                  ? item[1].toString()
                  : ''
                : '',
            dataType: 'string',
            tag: item.tag,
            isKey: false,
            route: tab.toString(),
          });
        }
      });

      return fields;
    };

    const getSchema = async (res: { [x: string]: any; '@assetType': any }) => {
      const assetType =
        res['@assetType'].indexOf('[]') >= 0
          ? res['@assetType'].replace('[]', '')
          : res['@assetType'];
      const {
        data,
      }: {
        data: { props: { label: string; isKey: boolean; tag: string }[] };
      } = await instance.post('/api/query/getSchema/', {
        assetType,
      });

      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      const fields: FieldView[] = await setFields(data.props, res);

      return fields;
    };

    const getItem = async (asset: string, key: string) => {
      try {
        let { data } = await instance.post(`/api/query/readAsset`, {
          key: { '@assetType': asset, '@key': key },
          resolve: true,
        });
        if (data['@hash']) {
          const res = await instance.post(
            `/api/query/readAsset?collections=${collection}`,
            { key: { '@assetType': asset, '@key': key }, resolve: 'true' },
          );
          if (data['@hash']) {
            setIsPrivate(true);
          }
          if (res && res.data) {
            data = res.data;
          }
        }
        const result = await getSchema(data);
        return result;
      } catch {
        try {
          const { data } = await instance.post(
            `/api/query/readAsset?collections=${collection}`,
            {
              key: { '@assetType': asset, '@key': key },
              resolve: 'true',
            },
          );
          const result = await getSchema(data);
          return result;
        } catch {}
        toast.error('Error receiving asset information');
      }

      return [];
    };

    const setFields = async (
      schemas: { label: string; tag: string }[],
      data: { [x: string]: any; '@assetType'?: string },
    ): Promise<FieldView[]> => {
      const results = Promise.all(
        schemas.map(async (item: any) => {
          const getValue = async () => {
            const dropDownValue = globals.getDropDownValues(item.dataType);
            if (dropDownValue) {
              const rawValue = data[item.tag];

              // Handle array dropdown values (e.g. []permission) — return array of labels
              if (Array.isArray(rawValue)) {
                return rawValue.map((val: any) => {
                  const entry = Object.entries(dropDownValue).find(
                    ([k, v]) => k === val || v === val,
                  );
                  return entry ? String(entry[1]) : String(val);
                });
              }

              const entrie = Object.entries(dropDownValue || {}).find(
                ([k, v]) => k === rawValue || v === rawValue,
              );

              if (entrie) return String(entrie[1]) || entrie[0];

              return '';
            }
            if (
              item.dataType.indexOf('->') >= 0 &&
              typeof data[item.tag] !== 'undefined' &&
              data[item.tag]['@key']
            ) {
              await getItem(
                item.dataType.replace('->', ''),
                data[item.tag]['@key'],
              );
            }

            switch (item.dataType) {
              case 'datetime':
                return getISODateTimeString(data[item.tag]);
              case 'boolean':
                return typeof data[item.tag] === 'undefined'
                  ? ''
                  : `${data[item.tag]}`.toUpperCase();
              default:
                return data[item.tag] ? data[item.tag] : '';
            }
          };

          return {
            label: item.label,
            value: await getValue(),
            disabled: item.isKey,
            isKey: item.isKey,
            tag: item.tag,
            route: item.route,
            dataType: item.dataType,
          };
        }),
      );

      return results;
    };

    const getTitle = async () => {
      if (params.key === '@txres') {
        setTitle('Transaction result');
        return;
      }
      const type = params.key.substring(0, params.key.indexOf(':'));
      const { data } = await instance.post('/api/query/getSchema/', {
        assetType: type,
      });
      setTitle(data.label);
      setCollection(data.collection ? data.collection : type);
    };

    getTitle();

    (async () => {
      const asset = params.key.split(':')[0];
      if (params.key === '@txres') {
        if (location && location.search) {
          try {
            const res = queryString.parse(location.search).objres;
            const objres = JSON.parse(atob(res as string)).data;
            const result = objectToSchema(objres, 0);

            setLoading(false);
            setFieldState(result);
          } catch {
            toast.error('Error parsing parameters');
            // we must fix this
          }
        }
      } else if (asset) {
        const result = await getItem(asset, params.key);
        setLoading(false);
        setFieldState(result);
      }
    })();
  }, [collection, setCollection, match, params.key, location, globals]);

  if (loading)
    return (
      <LoadingContainer>
        <CircularProgress />
      </LoadingContainer>
    );

  return (
    <Container headerHeight={globals.headerHeight}>
      <QrCodeDialog
        openQRCode={openQRCode}
        closeQRCode={() => setOpenQRCode(false)}
        params={params}
      />
      <Paper>
        <Title>{title}</Title>
        {params.key && params.key.split(':').length === 2 ? (
          <div>
            {isPrivate ? (
              <Grid container>
                <Icon
                  className="bx bx-lock-alt"
                  style={{ color: '#000099', marginRight: '2%' }}
                />
                <Typography style={{ color: '#000099' }}>
                  Private asset - no reading permission
                </Typography>
              </Grid>
            ) : null}

            <Typography>@key</Typography>
            <Grid container>
              <Typography>{params.key}</Typography>
              <Button
                style={{ marginTop: '-1%', marginLeft: '1%' }}
                onClick={() => {
                  setOpenQRCode(true);
                }}
              >
                <Icon
                  className="bx bx-qr"
                  style={{ marginRight: '3px' }}
                />
              </Button>
            </Grid>
          </div>
        ) : null}

        {fieldState.map((item: any, idx) => (
          <FieldContainer key={`${item.tag}${idx}`}>
            <SingleField
              label={item.label}
              value={item.value}
              tag={item.tag}
              dataType={item.dataType}
              parent={parseInt(item.route)}
            />
            {item.value && (
              <Tooltip title={copied ? 'Copied!' : 'Copy'}>
                <CopyToClipboard
                  text={
                    item.dataType.startsWith('->')
                      ? `${item.value['@key']}`
                      : `${item.value}`
                  }
                >
                  <Icon
                    className="bx bx-copy"
                    onClick={() => setCopied(true)}
                    onMouseEnter={() => setCopied(false)}
                  />
                </CopyToClipboard>
              </Tooltip>
            )}
          </FieldContainer>
        ))}
        <Grid style={{ marginTop: '5%' }} container>
          <Grid item xs={8} />
          <Grid item xs={2}>
            <Button
              variant="outlined"
              onClick={() => {
                history.goBack();
              }}
            >
              Back
            </Button>
          </Grid>
          <Grid item xs={2}>
            <Button
              variant="outlined"
              onClick={() => {
                if (params.key !== '@txres') {
                  const type = params.key.substring(0, params.key.indexOf(':'));
                  if (type) {
                    history.push(`/edit/${type}/${params.key}`);
                  }
                }
              }}
            >
              <Icon
                className="bx bx-edit"
                style={{ marginRight: '3px' }}
              />
              Edit
            </Button>
          </Grid>
        </Grid>
      </Paper>
    </Container>
  );
};

export default ItemScreen;
